import { ActionTree } from "vuex";
import api from "@/api/signup/api";
import { IPromoCodesState } from "./types";
import { IRootState } from "@/store/types";
import { UI } from "@/store/ui";
import { PROMO_CODES_MUTATIONS } from "./mutations";
import { state as defaultState } from ".";
import { cloneDeep } from "@/helpers/object";
import { ERROR_MESSAGES } from "@/constants/errors";

export namespace PROMO_CODES_ACTIONS {
  export const CLEAR_DISCOUNTS_Z = "resetDiscountsZ";
  export const CLEAR_DISCOUNTS_CALTEX = "resetDiscountsCaltex";
  export const CLEAR_DISCOUNTS_CHALLENGE = "resetDiscountsChallenge";

  export const RESET_STATE = "resetState";
  export const CREATE_PROMO_OFFER = "createPromoOffer";
  export const UPDATE_PROMO_OFFER = "updatePromoOffer";
  export const LOAD_PROMO_OFFER_LIST = "loadPromoCodesList";
  export const LOAD_PROMO_OFFER = "loadPromoCode";
  export const LOAD_PROMO_OFFER_BY_OFFER_ID = "loadPromoCodeByOfferId";
}

export const actions: ActionTree<IPromoCodesState, IRootState> = {
  [PROMO_CODES_ACTIONS.RESET_STATE]({ commit }): void {
    commit(
      PROMO_CODES_MUTATIONS.SET_PROMO_CODE,
      cloneDeep(defaultState.promoCode)
    );
  },
  async [PROMO_CODES_ACTIONS.LOAD_PROMO_OFFER_LIST]({
    commit,
    dispatch
  }): Promise<void> {
    try {
      const response: any = await api.getPromoOffers();
      commit(PROMO_CODES_MUTATIONS.SET_PROMO_CODES_LIST, response.data.data);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-promo-offer-list",
          text: ERROR_MESSAGES.LOAD_PROMO_OFFER_LIST_ERROR,
          error
        },
        { root: true }
      );
    }
  },
  async [PROMO_CODES_ACTIONS.LOAD_PROMO_OFFER](
    { commit, dispatch },
    id: number
  ): Promise<void> {
    commit(PROMO_CODES_MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      const response: any = await api.getPromoOffer(id);
      // merge with defaultState incase there are field not returned by the API otherwise
      // the reactivity won't work properly
      const promoCode = Object.assign(
        cloneDeep(defaultState.promoCode),
        response.data
      );

      commit(PROMO_CODES_MUTATIONS.SET_PROMO_CODE, promoCode);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-promo-offer",
          text: ERROR_MESSAGES.LOAD_PROMO_OFFER_ERROR,
          error
        },
        { root: true }
      );
    }

    commit(PROMO_CODES_MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [PROMO_CODES_ACTIONS.LOAD_PROMO_OFFER_BY_OFFER_ID](
    { commit, dispatch },
    id: number
  ): Promise<void> {
    commit(PROMO_CODES_MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      const response: any = await api.getPromoOfferByOfferId(id);
      // merge with defaultState incase there are field not returned by the API otherwise
      // the reactivity won't work properly
      const promoCode = Object.assign(
        cloneDeep(defaultState.promoCode),
        response.data
      );

      commit(PROMO_CODES_MUTATIONS.SET_PROMO_CODE, promoCode);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-promo-offer",
          text: ERROR_MESSAGES.LOAD_PROMO_OFFER_BY_OFFER_ID_ERROR,
          error
        },
        { root: true }
      );
    }

    commit(PROMO_CODES_MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [PROMO_CODES_ACTIONS.CREATE_PROMO_OFFER]({
    commit,
    dispatch,
    state
  }): Promise<boolean> {
    if (!state.promoCode) {
      return false;
    }

    commit(PROMO_CODES_MUTATIONS.TOGGLE_IS_LOADING, true);
    let success = false;

    try {
      // legal and benefits need to be casted for now.
      const promoCode = cloneDeep(state.promoCode);
      await api.createPromoOffer(promoCode);

      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "created-promo-code",
          text: "Successfully created promo code"
        },
        { root: true }
      );

      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-create-promo-code",
          text: ERROR_MESSAGES.CREATE_PROMO_OFFER_ERROR,
          error
        },
        { root: true }
      );
    }

    commit(PROMO_CODES_MUTATIONS.TOGGLE_IS_LOADING, false);

    return success;
  },
  async [PROMO_CODES_ACTIONS.UPDATE_PROMO_OFFER](
    { commit, dispatch, state },
    id: number
  ): Promise<void> {
    if (!state.promoCode) {
      return;
    }

    commit(PROMO_CODES_MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      // legal and benefits need to be casted for now.
      const promoCode = cloneDeep(state.promoCode);
      await api.updatePromoOffer(promoCode, id);

      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "updated-promo-code",
          text: "Successfully updated promo code"
        },
        { root: true }
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-save-promo-code",
          text: ERROR_MESSAGES.UPDATE_PROMO_OFFER_ERROR,
          error
        },
        { root: true }
      );
    }

    commit(PROMO_CODES_MUTATIONS.TOGGLE_IS_LOADING, false);
  }
};
